import { EndPointDoc, Method } from '../apiref/apireftypes';
import { ApiRef } from '../fasades/apiref';
import { GenCodeCurl } from '../apiref/genCodeCurl';
import { GenCodeJavaScript } from '../apiref/genCodeJavaScript';
import { GenCodePython } from '../apiref/genCodePython';

function trainingExample(stream : boolean) : object
{
    return {
        "model"         : "MyCustomModel",
        "messages"      : [
            {
                "role"    : "system",
                "content" : "You are a helpful assistant."
            },
            {
                "role"    : "user",
                "content" : "What is the capital of France?"
            }
        ],
        "responses"     : [
            {
                "reward"   : 1.0,
                "response" : "The capital of France is Paris."
            },
            {
                "reward"   : -0.5,
                "response" : "I don't know."
            }
        ],
        "method"         : "gce",
        "learning_steps" : 2,
        "stream"         : stream,
        "learning_rate"  : 1e-05,
        "beta"           : 0.04,
        "epsilon"        : 0.2
    };
}

export function train() : EndPointDoc
{
    let model = <>
        <p>The identifier of the language model to use for training.</p>
        <p><strong>Note:</strong> This must be a model you yourself has created, the standard models Llama-3.1-8b or DeepSeek-R1-8b are readonly.
        So you have to first make a copy of these models, and then train them.</p>
        </>

    let stream = <>
        <p>Streaming allow you to see the results of individual training steps incrementally as it's generated, instread of getting a status report when training is compled.</p>
        <ul>
            <li>false - Returns the complete response only after training is finished</li>
            <li>true - Streams response chunks as the training processes, allowing for real-time display</li>
        </ul>
        </>


    let messages       = ApiRef.getType("Message[]");
    let responses      = ApiRef.getType("Response[]");
    let trainingMethod = ApiRef.getType("TrainingMethod");

    const learningSteps = (<>
        <p>The number of training iterations or optimization steps to perform during the fine-tuning process.
           Each step represents one update to the model's parameters. More learning steps generally lead to
           better model adaptation but increase training time and cost.</p>
        <h4>Considerations:</h4>

        <p>
        Do not use a to high value here, because you leaning one thing at a time, so a to high will result in overfitting,
        where the model memorizes training examples rather than generalizing. Instead use multiple calls with different training data to learn 
        the model new behaviour.</p>

        <p>Alternatively, you can use the <i>targetAccuracy</i> parameter to automatically stop training when the target accuracy is reached.</p>
        </>);

    const targetAccuracy = (<>
        <p>
        This parameter works together with <i>learningSteps</i>. During training, accuracy is measured as the percentage of words that match the target response. A word is considered to match if the model assigns it a probability above 50%.
        </p>

        <p>
        When this target accuracy is reached, training stops automatically. Training will also stop when the maximum number of training steps (specified by <i>learningSteps</i>) has been reached, whichever comes first.
        </p>
        </>);

    const learningRate = (<>
        <strong>Range:</strong> Positive decimal (typically between 1E-4 and 1E-7)

        <p>Controls how much the model's parameters are adjusted during each training step. The learning rate determines the size of the steps taken during optimization - larger values make bigger updates, while smaller values make more cautious adjustments.</p>

        <p>If not specified, the model will use its default learning rate, which has been pre-configured based on the model architecture and recommended training practices.</p>

        <p><strong>Behavior:</strong></p>
        <ul>
            <li>High learning rates (1E-4 - 1E-5) - Faster training but risk of instability or overshooting optimal parameters</li>
            <li>Low learning rates (1E-5 - 1E-6)  - Slower but more stable training, good for preserving pre-trained knowledge</li>
        </ul>

        <p><strong>Recommendations:</strong></p>
        <ul>
            <li>Use the model's default learning rate unless you have specific performance issues</li>
            <li>Lower learning rates are generally safer for fine-tuning pre-trained models to avoid catastrophic forgetting</li>
            <li>If training appears unstable or loss values spike, reduce the learning rate</li>
            <li>If training progresses too slowly, carefully increase the learning rate</li>
        </ul>
        </>);


    const epsilon = (<>
        <p>This is used in the GRPO training method. It is the clipping parameter that prevents destructively large policy updates by constraining the probability ratio between new and old policies to [1-epsilon, 1+epsilon]. Default is 0.2.
        </p></>);

    const beta = (<>
        <p>A critical hyperparameter that controls how much the model is allowed to deviate from its reference policy during training. The role and recommended values of beta differ significantly between DPO and GRPO methods.</p>

        <h3>Beta in DPO (Direct Preference Optimization)</h3>
        <p>In DPO, beta is the primary trade-off parameter that controls the strength of the preference learning signal relative to staying close to the reference model.</p>
        <p><strong>How it works:</strong></p>
        <ul>
            <li>Beta appears in the DPO loss function as a coefficient that weights the log-probability ratios between preferred and non-preferred responses</li>
            <li>Higher beta values make the model more conservative, keeping it closer to the reference policy and making smaller adjustments based on preferences</li>
            <li>Lower beta values allow more aggressive optimization toward human preferences, potentially diverging more from the reference model</li>
        </ul>
        <p><strong>Recommended ranges for DPO:</strong></p>
        <ul>
            <li>beta = 0.1 to 0.3 - Standard range for most DPO fine-tuning tasks</li>
            <li>beta = 0.1 - More aggressive preference learning, allows significant deviation from reference</li>
            <li>beta = 0.5 - More conservative, maintains stronger alignment with reference model behavior</li>
        </ul>

        <h3>Beta in GRPO (Group Relative Policy Optimization)</h3>
        <p>In GRPO, beta serves as a KL divergence penalty coefficient for regularization, but is typically set much lower than in DPO or disabled entirely.</p>
        <p><strong>How it works:</strong></p>
        <ul>
            <li>When beta = 0.0 (default), no KL penalty is applied and the reference model is not loaded, reducing memory usage</li>
            <li>When beta &lt; 0.0, a penalty term prevents the model from diverging too far from the reference policy</li>
            <li>This regularization helps preserve general capabilities while learning new tasks</li>
        </ul>
        <p><strong>Note:</strong> For GRPO, we highly recomend setting this to 0.0, because there is a problem with Kl divergence making the model less 
        stabile when it is applied.</p>
        </>);

    const exampleUrl              = "v1/train";
    const exampleBody             = trainingExample(false);
    const exampleBodyStreaming    = trainingExample(true);
    const genCurl                 = new GenCodeCurl(Method.Post, exampleUrl, exampleBody);
    const genPython               = new GenCodePython(Method.Post, exampleUrl, exampleBody);
    const genJavaScript           = new GenCodeJavaScript(Method.Post, exampleUrl, exampleBody);
    const genCurlStreaming        = new GenCodeCurl(Method.Post, exampleUrl, exampleBodyStreaming);
    const genPythonStreaming      = new GenCodePython(Method.Post, exampleUrl, exampleBodyStreaming);
    const genJavaScriptStreaming  = new GenCodeJavaScript(Method.Post, exampleUrl, exampleBodyStreaming);
    const codeCurl                = genCurl.generate();
    let codePython                = genPython.generateImport();
    codePython                   += genPython.generateRequest();
    codePython                   += genPython.readResponse();
    let codeJavaScript            = genJavaScript.generateRequest();
    codeJavaScript               += genJavaScript.readResponse();
    const codeCurlStreaming       = genCurlStreaming.generate();
    let codePythonStreaming       = genPythonStreaming.generateImport();
    codePythonStreaming          += genPythonStreaming.generateRequest();
    codePythonStreaming          += genPythonStreaming.readResponseStreamingTraining();
    let codeJavaScriptStreaming   = genJavaScriptStreaming.generateRequest();
    codeJavaScriptStreaming      += genJavaScriptStreaming.readResponseStreamingTraining();

    return {
        "title"          : "Train",
        "method"         : Method.Post,
        "url"            : "v1/train",
        "docurl"         : "train",
        "seoDescription" : "Fine-tune your custom AI models using examples with scores to teach the model desired behaviors.",
        "description" : <>
            <p>Fine-tune a language model by adjusting its weights to make it more likely to produce the results you want.
               Training modifies the model's internal parameters based on your provided examples, enabling it to learn new
               behaviors, preferences, and response patterns.</p>
            <p>To train the model, you provide a conversation context (messages) along with multiple possible responses, 
               each with a reward value indicating how desirable that response is. The model learns from these examples
               by updating its weights to favor responses with higher rewards, gradually improving its ability to 
               generate the types of outputs you prefer.</p>
            <p>This process allows you to customize the model's behavior for specific tasks, styles, or preferences without
               needing to build a model from scratch.</p>
        </>,
        "parameters"  : [
            {
                "name"        : "model",
                "type"        : "string",
                "description" : model,
                "optional"    : false
            },
            {
                "name"        : "messages",
                "type"        : "Message[]",
                "description" : messages.description,
                "optional"    : false
            },
            {
                "name"        : "response",
                "type"        : "Response[]",
                "description" : responses.description,
                "optional"    : false
            },
            {
                "name"        : "stream",
                "type"        : "boolean",
                "description" : stream,
                "optional"    : true,
                "default"     : "false"
            },
            {
                "name"        : "method",
                "type"        : "TrainingMethod",
                "description" : trainingMethod.description,
                "optional"    : false,
            },
            {
                "name"        : "learning_steps",
                "type"        : "number",
                "description" : learningSteps,
                "optional"    : false,
            },
            {
                "name"        : "target_accuracy",
                "type"        : "number",
                "description" : targetAccuracy,
                "optional"    : true,
            },
            {
                "name"        : "learning_rate",
                "type"        : "number",
                "description" : learningRate,
                "optional"    : true,
            },
            {
                "name"        : "epsilon",
                "type"        : "number",
                "description" : epsilon,
                "optional"    : true,
            },
            {
                "name"        : "beta",
                "type"        : "number",
                "description" : beta,
                "optional"    : true,
            }
        ],
        "examples" : [
            {
                "url"            : exampleUrl,
                "title"          : "standard",
                "body"           : exampleBody,
                "output"         : [{
                    "performance" : [
                        { "id": 1, "advantage": 1, "accuracy": 1, "loss": 0.0167236 },
                        { "id": 2, "advantage": -1, "accuracy": 0.333333, "loss": 5.15625 }
                    ],
                    "usage" : {
                        "input_tokens" : 28,
                        "output_tokens" : 14,
                        "num_samples" : 2,
                        "steps" : 2,
                        "training_tokens" : 350,
                        "duration_ms" : 4294
                    }
                }],
                "responseType"   : ApiRef.getType("TrainResponse"),
                "codeCurl"       : codeCurl,
                "codePython"     : codePython,
                "codeJavaScript" : codeJavaScript
            },
            {
                "url"            : exampleUrl,
                "title"          : "streaming",
                "body"           : exampleBodyStreaming,
                "output"         : [
                    { "message": "Loading model llama-3.1-8b", "type": "info" },
                    { "message": "Loading tokenizer", "type": "info" },
                    { "performance": { "accuracy": 1, "advantage": 1, "id": 1, "loss": 0.0167236, "step_no": 0 }, "type": "performance" },
                    { "performance": { "accuracy": 0.333333, "advantage": -1, "id": 2, "loss": 5.15625, "step_no": 0 }, "type": "performance" },
                    { "performance": { "accuracy": 1, "advantage": 1, "id": 1, "loss": 0.0167236, "step_no": 1 }, "type": "performance" },
                    { "performance": { "accuracy": 0.333333, "advantage": -1, "id": 2, "loss": 5.15625, "step_no": 1 }, "type": "performance" },
                    { "usage": { "duration_ms": 3696, "input_tokens": 28, "num_samples": 2, "output_tokens": 14, "steps": 2, "training_tokens": 140 }, "type": "usage" }
                ],
                "responseType"   : ApiRef.getType("TrainStreamingChunk[]"),
                "codeCurl"       : codeCurlStreaming,
                "codePython"     : codePythonStreaming,
                "codeJavaScript" : codeJavaScriptStreaming
            }

        ]
    };
}

